import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import moment from "moment";
import { styled } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Toolbar from "@mui/material/Toolbar";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import LocationOnRoundedIcon from "@mui/icons-material/LocationOnRounded";
import PeopleRoundedIcon from "@mui/icons-material/PeopleRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import EditNoteRoundedIcon from "@mui/icons-material/EditNoteRounded";
import InfoRoundedIcon from "@mui/icons-material/InfoRounded";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import Alert from "@mui/material/Alert";
import EventBusyRoundedIcon from "@mui/icons-material/EventBusyRounded";
import Rating from "@mui/material/Rating";
import { canReview, submitReview, getMyReview, MAX_COMMENT_LENGTH } from "../../../utils/reviews";
import { useAuth } from "../../../context";
import { useApp } from "../../../context";
import PageTemplate from "../../components/PageTemplate";
import { normalizeBooking } from "../../../utils/booking";
import { computeBookingTotals, formatMoney, toCount } from "../../../utils/pricing";
import TripCompanions from "../../components/TripCompanions";
import TripComments from "../../components/TripComments";
import { buildBookingSnapshot, canInviteToBooking, whyNotShareable } from "../../../utils/tripSharing";
import { publishSnapshotToInvites, sendFinalCopies } from "../../../utils/tripInvites";
import { SHARED_BOOKING_SUBJECT, buildSharedBookingEmailHtml } from "../../../utils/tripEmails";

const Main = styled("div")(({ theme }) => ({
  flexGrow: 1,
  height: "100vh",
  overflowY: "auto",
  padding: theme.spacing(0),
  paddingBottom: theme.spacing(1),
  background: theme.palette.background.default,
}));

const Item = styled(Paper)(({ theme }) => ({
  ...theme.typography.body1,
  textAlign: "left",
  color: theme.palette.text.secondary,
  padding: theme.spacing(2),
  margin: theme.spacing(2),
}));

// Accepts the Date objects returned by normalizeBooking, and still tolerates
// a raw Firestore Timestamp in case a caller passes one through directly.
const formatDate = (d) => {
  if (!d) return "";
  if (d instanceof Date) return moment(d).format("DD MMM YYYY");
  if (d.seconds !== undefined) return moment(d.seconds * 1000).format("DD MMM YYYY");
  if (typeof d.toDate === "function") return moment(d.toDate().getTime()).format("DD MMM YYYY");
  return String(d);
};

export default function BookingPage() {
  const { id } = useParams();
  const { getBooking, updateBooking } = useApp();
  const { user } = useAuth();

  const [booking, setBooking] = useState(null);

  // Sharing. A booking has no "who's coming with you" answer, so the equivalent
  // gate is its party size — see canInviteToBooking.
  // Both halves are kept: the thread to show the conversation, and the guest
  // list so "we will send them the final copy" is a promise this page can
  // actually keep. Dropping `invites` here is how that promise was broken.
  const [share, setShare] = useState(null);
  const [invites, setInvites] = useState([]);
  const [sendingCopies, setSendingCopies] = useState(false);
  const [copyNotice, setCopyNotice] = useState('');
  const handleShareReady = React.useCallback((nextShare, list) => {
    setShare(nextShare || null);
    setInvites(list || []);
  }, []);

  /**
   * Sends everyone on the guest list the booking details.
   *
   * A booking has no "finalise" moment the way an itinerary does — it is the
   * operator who moves it, and the traveller is never told when. So rather
   * than invent a trigger, the organiser presses this when the booking is
   * settled enough to be worth circulating. The snapshot is republished first
   * so the copy and the link agree.
   */
  const handleSendCopies = async () => {
    if (!share?.shareId || !booking) return;
    setSendingCopies(true);
    setCopyNotice('');
    try {
      const snapshot = buildBookingSnapshot(booking);
      await publishSnapshotToInvites({ shareId: share.shareId, snapshot, invites });
      const sent = await sendFinalCopies({
        invites,
        subject: SHARED_BOOKING_SUBJECT,
        buildHtml: (invite) =>
          buildSharedBookingEmailHtml({ snapshot, invite, organiser: 'Your trip organiser' }),
      });
      setCopyNotice(
        sent === 0
          ? 'Nobody on your guest list has given an email address, so there was nothing to send. Their links are up to date.'
          : sent === 1
            ? 'Sent to 1 person.'
            : `Sent to ${sent} people.`
      );
    } catch (error) {
      console.error('handleSendCopies: failed', error);
      setCopyNotice('We could not send those just now. Please try again.');
    } finally {
      setSendingCopies(false);
    }
  };
  const [loading, setLoading] = useState(true);

  // Editable states
  const [adults, setAdults] = useState(1);
  const [children, setChildren] = useState(0);
  const [infants, setInfants] = useState(0);
  const [info, setInfo] = useState("");
  const [saving, setSaving] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState("");
  const [myReview, setMyReview] = useState(null);
  const [reviewRating, setReviewRating] = useState(0);
  const [reviewComment, setReviewComment] = useState("");
  const [reviewSaving, setReviewSaving] = useState(false);
  const [reviewError, setReviewError] = useState("");
  const [reviewSaved, setReviewSaved] = useState(false);

  useEffect(() => {
    async function fetchBooking() {
      setLoading(true);
      const data = await getBooking(id);
      if (data) {
        // Normalize so legacy and v2 documents expose the same fields.
        const normalized = normalizeBooking({ id, ...data });
        setBooking(normalized);
        setAdults(normalized.travelers.adults);
        setChildren(normalized.travelers.children);
        setInfants(normalized.travelers.infants);
        setInfo(normalized.info || "");
      }
      setLoading(false);
    }
    fetchBooking();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  /**
   * Calling off a booking.
   *
   * Only while it is still PENDING, which is not a UI preference —
   * firestore.rules gates the traveller's whole update branch on
   * `resource.data.status == 'PENDING'`. Once a provider has confirmed it,
   * cancelling is their decision, because by then they may have turned other
   * business away for the date.
   *
   * Confirmed before it happens, and deliberately not undoable from here:
   * CANCELLED is terminal for a traveller, so a mis-tap would otherwise cost
   * them the booking with no way back except asking the provider.
   */
  // A traveller may edit what they wrote, so an existing review is loaded and
  // becomes the starting state of the form rather than a second one being
  // created — the review's document id IS the booking id, so writing again
  // replaces it either way.
  useEffect(() => {
    let live = true;
    if (!booking || !user || !canReview(booking, user.uid)) return undefined;
    getMyReview(booking.id).then((existing) => {
      if (!live || !existing) return;
      setMyReview(existing);
      setReviewRating(Number(existing.rating) || 0);
      setReviewComment(existing.comment || "");
    });
    return () => {
      live = false;
    };
  }, [booking, user]);

  const handleSubmitReview = async () => {
    if (!booking || !user) return;
    setReviewSaving(true);
    setReviewError("");
    try {
      const saved = await submitReview({
        booking,
        uid: user.uid,
        authorName: user.displayName || null,
        rating: reviewRating,
        comment: reviewComment,
      });
      setMyReview(saved);
      setReviewSaved(true);
    } catch (error) {
      console.error("Failed to submit review:", error);
      // firestore.rules refuse a review without a REDEEMED booking, so the
      // usual cause of a failure here is a status that moved underneath the
      // page rather than anything the traveller typed.
      setReviewError(
        error?.message?.startsWith("Please choose") || error?.message?.startsWith("You can only")
          ? error.message
          : "We could not save your review just now. Please try again."
      );
    } finally {
      setReviewSaving(false);
    }
  };

  const handleCancel = async () => {
    if (!booking) return;
    setCancelling(true);
    setCancelError("");
    try {
      await updateBooking(id, { status: "CANCELLED" });
      setBooking({ ...booking, status: "CANCELLED" });
      setCancelOpen(false);
    } catch (error) {
      console.error("Failed to cancel booking:", error);
      // Shown rather than swallowed. A silent failure here leaves someone
      // believing they have cancelled, and turning up expecting nothing.
      setCancelError("We could not cancel that just now. Please try again.");
    } finally {
      setCancelling(false);
    }
  };

  const handleSave = async () => {
    if (!booking) return;
    setSaving(true);
    try {
      const travelers = {
        adults: Math.max(1, toCount(adults)),
        children: toCount(children),
        infants: toCount(infants),
      };

      // Changing the guest count changes the price. Recompute the money
      // alongside it so the traveler's total and the provider's payout
      // never drift from the booking's own traveler counts.
      const totals = computeBookingTotals({
        unitCost: booking.unitCost,
        priceUnit: booking.priceUnit,
        currency: booking.currency,
        nights: booking.nights,
        travelers,
        commissionRate: booking.commissionRate,
      });

      const updates = {
        travelers,
        guests: travelers.adults + travelers.children + travelers.infants,
        info,
        quantity: totals.quantity,
        subtotal: totals.subtotal,
        commissionDeducted: totals.commissionDeducted,
        netMerchantPay: totals.netMerchantPay,
        totalPaid: totals.totalPaid,
      };

      await updateBooking(id, updates);
      setBooking({ ...booking, ...updates });
    } catch (error) {
      console.error("Failed to save booking:", error);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <PageTemplate>
        <Main>
          <Toolbar sx={{ marginBottom: "1em" }} />
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        </Main>
      </PageTemplate>
    );
  }

  if (!booking) {
    return (
      <PageTemplate>
        <Main>
          <Toolbar sx={{ marginBottom: "1em" }} />
          <Box sx={{ px: { xs: 2, sm: 3 } }}>
            <Button component={Link} to="/booking-list" variant="text" startIcon={<ChevronLeftIcon />}>
              Back to Bookings
            </Button>
          </Box>
          <Item elevation={1}>
            <Typography color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
              Booking not found.
            </Typography>
          </Item>
        </Main>
      </PageTemplate>
    );
  }

  const { status, bookingType, createdAt, startDate, endDate } = booking;
  // Status is normalized to UPPERCASE. This previously compared against
  // "confirmed" and so never locked a confirmed booking from editing.
  const isFinalized = status !== "PENDING";

  return (
    <PageTemplate>
      <Main>
        <Toolbar sx={{ marginBottom: "1em" }} />
        <Box sx={{ px: { xs: 2, sm: 3 } }}>
          <Button component={Link} to="/booking-list" variant="text" startIcon={<ChevronLeftIcon />}>
            Back
          </Button>
        </Box>

        <Item elevation={1}>
          <Typography component="h1" variant="h5" sx={{ fontWeight: 'bold', mb: 1, color: "text.primary" }}>
            Booking Details
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 1 }}>
            Booked on: {formatDate(createdAt) || "—"}
          </Typography>

          <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1, mb: 2 }}>
            {booking.location && (
              <Typography sx={{ display: "flex", alignItems: "center", fontWeight: 600, color: "text.primary", mr: 0.5 }}>
                <LocationOnRoundedIcon sx={{ fontSize: "1.1rem", mr: 0.5, color: "text.secondary" }} />
                {booking.location}
              </Typography>
            )}
            {bookingType && <Chip label={bookingType} size="small" color="primary" variant="outlined" />}
            {status && (
              <Chip
                label={status}
                size="small"
                // DECLINED is "default", not "error": the operator turning a
                // request down is an ordinary outcome, not a failure, and it
                // must not read as loudly as a cancelled confirmed booking.
                // "warning" is reserved for PENDING — anything unrecognised
                // landing there would look like it is still being decided.
                color={
                  status === "CONFIRMED" || status === "REDEEMED"
                    ? "success"
                    : status === "CANCELLED"
                    ? "error"
                    : status === "DECLINED"
                    ? "default"
                    : "warning"
                }
                variant="filled"
              />
            )}
          </Box>

          <Box sx={{ mt: 2, p: 2, bgcolor: "background.paper", borderRadius: 1, border: "1px solid", borderColor: "divider" }}>
            <Typography variant="h6" sx={{ color: "text.primary", mb: 1 }}>
              {booking.listingName}
            </Typography>
            {booking.listingType && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                {booking.listingType}
              </Typography>
            )}
            <Box sx={{ mt: 1.5, display: "flex", flexDirection: "column", gap: 0.5 }}>
              <Typography variant="body2" color="text.secondary">
                {formatMoney(booking.unitCost, booking.currency)} &times; {booking.quantity}
                {booking.guests > (booking.travelers.adults + booking.travelers.children)
                  ? " (infants travel free)"
                  : ""}
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                Total: {formatMoney(booking.totalPaid, booking.currency)}
              </Typography>
            </Box>
            {booking.contactEmail && (
              <Typography variant="body2" color="text.primary" sx={{ mt: 1 }}>
                Contact: {booking.contactEmail}
              </Typography>
            )}
          </Box>

          {startDate && (
            <Box sx={{ mt: 3 }}>
              <Typography sx={{ fontWeight: 600, color: "text.primary", mb: 1 }}>
                Travel Dates
              </Typography>
              <Typography color="text.secondary">
                {formatDate(startDate)}
                {endDate && endDate.getTime() !== startDate.getTime() ? ` - ${formatDate(endDate)}` : ""}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {booking.nights} night{booking.nights === 1 ? "" : "s"}
              </Typography>
            </Box>
          )}
        </Item>

        <Item elevation={1}>
          <Typography sx={{ display: "flex", alignItems: "center", fontWeight: 600, color: "text.primary", mb: 2 }}>
            <PeopleRoundedIcon sx={{ fontSize: "1.1rem", mr: 0.5, color: "text.secondary" }} />
            Number of Travelers
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 2 }}>
            <TextField
              disabled={isFinalized}
              id="adults"
              label="Adults"
              variant="outlined"
              value={adults}
              type="number"
              onChange={(e) => setAdults(e.target.value)}
              sx={{ width: 100 }}
            />
            <TextField
              disabled={isFinalized}
              id="children"
              label="Children"
              variant="outlined"
              value={children}
              type="number"
              onChange={(e) => setChildren(e.target.value)}
              sx={{ width: 100 }}
            />
            <TextField
              disabled={isFinalized}
              id="infants"
              label="Infants"
              variant="outlined"
              value={infants}
              type="number"
              onChange={(e) => setInfants(e.target.value)}
              sx={{ width: 100 }}
            />
          </Box>
        </Item>

        <Item elevation={1}>
          <Typography sx={{ display: "flex", alignItems: "center", fontWeight: 600, color: "text.primary", mb: 2 }}>
            <EditNoteRoundedIcon sx={{ fontSize: "1.2rem", mr: 0.5, color: "text.secondary" }} />
            Additional Information
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
             <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center' }}>
               <InfoRoundedIcon sx={{ fontSize: '1rem', mr: 0.5 }} />
               Add any special requests, dietary requirements, or notes for your booking.
             </Typography>
            <TextField
              disabled={isFinalized}
              id="info"
              label="Booking Notes"
              multiline
              rows={4}
              variant="outlined"
              value={info}
              onChange={(e) => setInfo(e.target.value)}
              fullWidth
            />
          </Box>
        </Item>

        {/* Who else is on this booking.
            Deliberately NOT gated on status: sharing a CONFIRMED or REDEEMED
            booking is the ordinary case — people split a bill after the fact —
            and the share lives in its own collection precisely so it does not
            need a write to a locked booking document. */}
        {user && booking && (
          <Box sx={{ mx: -2 }}>
            <TripCompanions
              ownerUid={user.uid}
              tripId={booking.id}
              tripType="BOOKING"
              shareable={canInviteToBooking(booking)}
              blockedReason={whyNotShareable(booking, 'BOOKING')}
              buildSnapshot={() => buildBookingSnapshot(booking)}
              onShareReady={handleShareReady}
            />
          </Box>
        )}

        {user && share?.threadId && (
          <>
            <Item elevation={1}>
              <Typography sx={{ fontWeight: 600, color: "text.primary", mb: 1 }}>
                Send everyone the details
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Emails the current booking details to everyone on your guest list who gave an
                address, and refreshes what their links show.
              </Typography>
              {copyNotice && (
                <Alert severity="info" sx={{ mb: 2 }} onClose={() => setCopyNotice('')}>
                  {copyNotice}
                </Alert>
              )}
              <Button variant="outlined" onClick={handleSendCopies} disabled={sendingCopies}>
                {sendingCopies ? 'Sending…' : 'Send the details'}
              </Button>
            </Item>

            <Box sx={{ mx: -2 }}>
              <TripComments
                threadId={share.threadId}
                inviteId={null}
                authorName="Trip organiser"
                canModerate
                // A booking nobody can act on any more is a thread nobody needs
                // to keep adding to.
                readOnly={['CANCELLED', 'DECLINED'].includes(String(booking?.status || '').toUpperCase())}
              />
            </Box>
          </>
        )}

        {/* Only after the stay. firestore.rules require a REDEEMED booking
            owned by the caller, so offering this any earlier would show a
            form that cannot be submitted. */}
        {user && canReview(booking, user.uid) && (
          <Item elevation={1}>
            <Typography component="h2" variant="h6" sx={{ fontWeight: 'bold', mb: 0.5, color: "text.primary" }}>
              {myReview ? "Your review" : "How was it?"}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {myReview
                ? "You can change this at any time — it replaces what you wrote before."
                : "Only travellers who completed a trip can review it, so this counts for a lot."}
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Rating
                name="review-rating"
                value={reviewRating}
                size="large"
                onChange={(_, value) => { setReviewRating(value || 0); setReviewSaved(false); }}
              />

              <TextField
                id="review-comment"
                label="Anything you would tell another traveller?"
                multiline
                rows={3}
                variant="outlined"
                value={reviewComment}
                onChange={(e) => { setReviewComment(e.target.value); setReviewSaved(false); }}
                inputProps={{ maxLength: MAX_COMMENT_LENGTH }}
                helperText={`${reviewComment.length}/${MAX_COMMENT_LENGTH}`}
                fullWidth
              />

              {reviewError && <Alert severity="error">{reviewError}</Alert>}
              {reviewSaved && !reviewError && (
                <Alert severity="success">
                  Thank you — your review is on the listing now.
                </Alert>
              )}

              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  onClick={handleSubmitReview}
                  disabled={reviewSaving || !reviewRating}
                  startIcon={reviewSaving ? <CircularProgress size={18} /> : null}
                >
                  {reviewSaving ? "Saving..." : myReview ? "Update review" : "Post review"}
                </Button>
              </Box>
            </Box>
          </Item>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap', px: { xs: 2, sm: 3 }, mb: 4 }}>
          {/* Only while PENDING — firestore.rules gates the traveller's whole
              update branch on that, so a button shown at any other point
              would fail rather than do nothing. */}
          {!isFinalized ? (
            <Button
              variant="outlined"
              color="error"
              disabled={cancelling || saving}
              onClick={() => { setCancelError(""); setCancelOpen(true); }}
              startIcon={<EventBusyRoundedIcon />}
              size="large"
            >
              Cancel booking
            </Button>
          ) : (
            <Box />
          )}

          <Button
            variant="contained"
            disabled={isFinalized || saving}
            onClick={handleSave}
            startIcon={saving ? <CircularProgress size={20} /> : <SaveRoundedIcon />}
            size="large"
          >
            {saving ? "Saving..." : "Save Changes"}
          </Button>
        </Box>

        <Dialog open={cancelOpen} onClose={() => (cancelling ? null : setCancelOpen(false))}>
          <DialogTitle>Cancel this booking?</DialogTitle>
          <DialogContent>
            <DialogContentText>
              This tells {booking.name || "the operator"} you will not be coming
              {startDate ? ` on ${moment(startDate).format("D MMM YYYY")}` : ""}. It cannot be
              undone here — you would need to make a new booking.
            </DialogContentText>
            {cancelError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {cancelError}
              </Alert>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCancelOpen(false)} disabled={cancelling}>
              Keep booking
            </Button>
            <Button
              onClick={handleCancel}
              color="error"
              variant="contained"
              disabled={cancelling}
              startIcon={cancelling ? <CircularProgress size={18} /> : null}
            >
              {cancelling ? "Cancelling..." : "Yes, cancel it"}
            </Button>
          </DialogActions>
        </Dialog>
      </Main>
    </PageTemplate>
  );
}
